import { useEffect, useState, type ReactNode, type MouseEvent } from 'react';
import type { Lang } from './lib/countries';
import { SUPPORTED_LANGS } from './i18n';
import { slugOf, bySlug } from './lib/country';

export type ModeKey = 'classic' | 'flag' | 'outline' | 'connections' | 'draw' | 'trivia';
export type View = 'game' | 'leaderboard' | 'profile' | 'privacy' | 'terms' | 'settings' | 'country' | 'countries';

export interface Route {
  lang: Lang;
  mode: ModeKey;
  endless: boolean;
  view: View;
  userId?: string;
  slug?: string;
}

// 本地化路径段名(讲究版):详情段 / 列表段
const SEG: Record<Lang, { detail: string; list: string }> = {
  en: { detail: 'country', list: 'countries' },
  es: { detail: 'pais', list: 'paises' },
  fr: { detail: 'pays', list: 'pays' },
  de: { detail: 'land', list: 'laender' },
  pt: { detail: 'pais', list: 'paises' },
  it: { detail: 'paese', list: 'paesi' },
  tr: { detail: 'ulke', list: 'ulkeler' },
};

/** 路径 -> 路由。英文无前缀;classic 走根路径;末段 unlimited 表示无尽模式;另有 leaderboard / u/:id / privacy 页 */
export function parseRoute(pathname: string = location.pathname): Route {
  const segs = pathname.split('/').filter(Boolean);
  let lang: Lang = 'en';
  if (segs[0] && (SUPPORTED_LANGS as string[]).includes(segs[0])) {
    lang = segs.shift() as Lang;
  }
  if (segs[0] === 'leaderboard') return { lang, mode: 'classic', endless: false, view: 'leaderboard' };
  if (segs[0] === 'u') return { lang, mode: 'classic', endless: false, view: 'profile', userId: segs[1] };
  if (segs[0] === 'privacy') return { lang, mode: 'classic', endless: false, view: 'privacy' };
  if (segs[0] === 'terms') return { lang, mode: 'classic', endless: false, view: 'terms' };
  if (segs[0] === 'settings') return { lang, mode: 'classic', endless: false, view: 'settings' };
  const seg = SEG[lang];
  if (segs[0] === seg.detail && segs[1]) return { lang, mode: 'classic', endless: false, view: 'country', slug: segs[1] };
  if (segs[0] === seg.list) return { lang, mode: 'classic', endless: false, view: 'countries' };

  let endless = false;
  if (segs[segs.length - 1] === 'unlimited') {
    endless = true;
    segs.pop();
  }
  let mode: ModeKey = 'classic';
  if (segs[0] === 'flag' || segs[0] === 'outline' || segs[0] === 'connections' || segs[0] === 'draw' || segs[0] === 'trivia')
    mode = segs[0];
  return { lang, mode, endless, view: 'game' };
}

/** 路由 -> 路径 */
export function buildPath(lang: Lang, mode: ModeKey, endless = false): string {
  const parts: string[] = [];
  if (lang !== 'en') parts.push(lang);
  if (mode !== 'classic') parts.push(mode);
  if (endless) parts.push('unlimited');
  return '/' + parts.join('/');
}

const langPrefix = (lang: Lang) => (lang === 'en' ? '' : '/' + lang);
export const buildLeaderboard = (lang: Lang): string => langPrefix(lang) + '/leaderboard';
export const buildProfile = (lang: Lang, id: string): string => langPrefix(lang) + '/u/' + id;
export const buildPrivacy = (lang: Lang): string => langPrefix(lang) + '/privacy';
export const buildTerms = (lang: Lang): string => langPrefix(lang) + '/terms';
export const buildSettings = (lang: Lang): string => langPrefix(lang) + '/settings';
export const buildCountryPath = (lang: Lang, cca3: string): string => langPrefix(lang) + '/' + SEG[lang].detail + '/' + slugOf(cca3, lang);
export const buildCountries = (lang: Lang): string => langPrefix(lang) + '/' + SEG[lang].list;

/** 把「当前路由」映射到目标语言的等价路径(语言切换时保持在同一页面) */
export function buildForLang(lang: Lang, r: Route): string {
  switch (r.view) {
    case 'leaderboard':
      return buildLeaderboard(lang);
    case 'privacy':
      return buildPrivacy(lang);
    case 'terms':
      return buildTerms(lang);
    case 'settings':
      return buildSettings(lang);
    case 'profile':
      return buildProfile(lang, r.userId || '');
    case 'countries':
      return buildCountries(lang);
    case 'country': {
      const c = r.slug ? bySlug(r.lang, r.slug) : null;
      return c ? buildCountryPath(lang, c.cca3) : buildCountries(lang);
    }
    default:
      return buildPath(lang, r.mode, r.endless);
  }
}

export function navigate(path: string): void {
  if (path !== location.pathname) {
    history.pushState(null, '', path);
    window.dispatchEvent(new Event('geodle:nav'));
    window.scrollTo(0, 0);
  }
}

export function useRoute(): Route {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const h = () => setPath(location.pathname);
    window.addEventListener('popstate', h);
    window.addEventListener('geodle:nav', h);
    return () => {
      window.removeEventListener('popstate', h);
      window.removeEventListener('geodle:nav', h);
    };
  }, []);
  return parseRoute(path);
}

export function Link({
  to,
  className,
  children,
  title,
  onClick,
}: {
  to: string;
  className?: string;
  children: ReactNode;
  title?: string;
  onClick?: () => void;
}) {
  function handle(e: MouseEvent) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onClick?.();
    navigate(to);
  }
  return (
    <a href={to} className={className} onClick={handle} title={title}>
      {children}
    </a>
  );
}
